import { Box } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import FullScreenLoader from 'components/FullScreenLoader';
import { useBloxBlockPageTitle } from 'hooks/useBloxDocumentTitle';
// import { HEADER_HEIGHT_V1 } from 'projectConstants';
import { getExandSideInputStatus, handleExpandSideInput } from 'redux/BuilderModeSlice';
import {
  FetchBlockOutputs,
  FetchFormulaList,
  FetchPlannerModeItems,
  FetchPlannerModeBlock,
  FetchPlannerModeModel,
  FetchScenarios,
  ResetTable,
  getBaseScenario,
  getPlannerBlock,
  getPlannerModel,
  getToggleInputPanel,
} from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import NewHeader from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/NewHeader';

import BuilderModeMainPanelV1 from './components/BuilderModeMainPanelV1';
import BuilderModeToolbarV1 from './components/toolbar/BuilderModeToolbarV1';
import BuilderModePageHeader from './BuilderModePageHeader';
import BuilderModeSubToolbar from './components/BuilderModeSubToolbar';
import { BuilderModeIndicatorPageV1 } from './inputPanel';
import { BUILDER_MODE_V1_LAYOUT } from './constants';
import { BuilderModeV1IndicatorIconProvider } from './icons/BuilderModeV1IndicatorIconContext';

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Builder body for UI revamp — same table/toolbar/panel as BuilderMode
 * without NavigationHeader or legacy PlannerModeHeader (toolbar is BuilderModePageHeader).
 */
const BuilderModeView: React.FC = () => {
  const { modelId, blockId } = useParams();
  const modelIdFromParameters = useParams().modelId;
  const [loader, setLoader] = useState(true);
  const toggleInputPanel = useSelector(getToggleInputPanel);
  const dispatch: AppDispatch = useDispatch();
  const blockDetails = useSelector(getPlannerBlock);
  const plannerModel = useSelector(getPlannerModel);
  const baseScenario = useSelector(getBaseScenario);
  const { pathname } = useLocation();
  const isExpand = useSelector(getExandSideInputStatus);

  // const [isTablet] = useMediaQuery('(max-width: 768px)');
  // const contentPaddingX = isTablet ? 4 : 7;

  useEffect(() => {
    const initialData = async () => {
      setLoader(true);
      dispatch(FetchPlannerModeModel(Number(modelId)));
      dispatch(FetchPlannerModeBlock(Number(blockId))).then(async () => {
        if (modelIdFromParameters) await dispatch(FetchScenarios(modelIdFromParameters));
        setLoader(false);
      });
    };
    initialData();
  }, [pathname, blockId, dispatch, modelId, modelIdFromParameters]);

  useEffect(() => {
    if (modelId && baseScenario?.id) {
      dispatch(FetchFormulaList({ modelId: Number(modelId), scenarioId: baseScenario.id }));
    }
  }, [dispatch, modelId, baseScenario?.id]);

  // Bootstraps the same table-side data that legacy `pages/BuilderMode/Table.tsx` used to fetch.
  useEffect(() => {
    if (!blockId || !baseScenario?.id || !blockDetails || !plannerModel) return;

    const startDateObject = new Date(baseScenario?.start_date);
    startDateObject.setMonth(startDateObject.getMonth() - 1);
    const yearFormat = `FY${startDateObject.getFullYear().toString().slice(-2)}`;
    const monthFormat = `${monthNames[startDateObject.getMonth()]}-${String(yearFormat).slice(-2)}`;

    if (blockDetails?.indicators?.length) {
      const idArray =
        blockDetails?.dimensions
          ?.filter((item: any) => item.name !== 'Time')
          .map((item: any) => item.id) ?? [];
      const dimensionIds = idArray.join(', ');
      dispatch(
        FetchBlockOutputs({
          blockId,
          params: {
            dim_id: dimensionIds,
            indicator_filter: 'all',
            scenario_id: baseScenario?.id,
          },
        }),
      );
    }

    const timeDim = blockDetails?.dimensions?.find((data: any) => data?.name === 'Time');
    dispatch(
      FetchPlannerModeItems({
        dimensionId: timeDim?.id,
        params: { scenario_id: baseScenario?.id },
        actuals:
          plannerModel?.time_properties?.time_granularity?.toLowerCase() === 'm'
            ? monthFormat
            : yearFormat,
      }),
    );
  }, [baseScenario?.id, baseScenario?.start_date, blockDetails, blockId, dispatch, plannerModel]);

  useBloxBlockPageTitle(loader ? undefined : blockDetails?.name, plannerModel?.name, 'Builder');

  useEffect(() => {
    return () => {
      dispatch(handleExpandSideInput(false));
      dispatch(ResetTable());
    };
  }, [dispatch]);

  if (loader) {
    return (
      <FullScreenLoader
        bgColor={'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)'}
        height={'100vh'}
      />
    );
  }

  const flexDirection: 'row' | 'row-reverse' = isExpand ? 'row-reverse' : 'row';

  return (
    <BuilderModeV1IndicatorIconProvider value={true}>
      <Box
        background={
          'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%) !important'
        }
        h='100%'
        minH={0}
        overflow='hidden'
        py={4}
        display='flex'
        flexDirection='column'
      >
        <NewHeader />
        <BuilderModePageHeader />
        <BuilderModeSubToolbar />
        <Box width='100%' flex={1} minH={0} gap={4} display='flex' flexDirection={flexDirection}>
          <Box
            width={toggleInputPanel ? (isExpand && toggleInputPanel ? '0%' : '75%') : '100%'}
            flex={1}
            minH={0}
            display={isExpand ? 'none' : 'flex'}
            flexDirection='column'
            overflow='hidden'
            px={BUILDER_MODE_V1_LAYOUT.horizontalPadding}
            pt='20px'
            gap='20px'
          >
            <BuilderModeToolbarV1 />
            <Box flex='1' minH={0} overflow='hidden' display='flex' flexDirection='column'>
              <BuilderModeMainPanelV1 />
            </Box>
          </Box>
          {toggleInputPanel && (
            <Box
              mt={4}
              className={`input-panel${toggleInputPanel ? ' visible' : ' hidden'}`}
              width={toggleInputPanel ? (isExpand && toggleInputPanel ? '100%' : '25%') : '0'}
              minW={0}
              flexShrink={0}
              h='100%'
              overflow='hidden'
            >
              <BuilderModeIndicatorPageV1 />
            </Box>
          )}
        </Box>
      </Box>
    </BuilderModeV1IndicatorIconProvider>
  );
};

export default withLDConsumer()(BuilderModeView);
